/**
 * Grouped section with an optional title, footer, and content.
 * Renders an optional uppercase title, a rounded card with separated rows,
 * and an optional footer caption below the card.
 */
import { Children, Fragment, type ReactNode } from "react";
import { View } from "react-native";

import { Separator } from "@/components/ui/separator";
import { Text } from "@/components/ui/text";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "./card";

export type SectionProps = {
    title?: string | React.ReactNode;
    footer?: string | React.ReactNode;
    children: ReactNode;
    className?: string;
    sectionClassName?: string;
    sectionContentClassName?: string;
    testID?: string;
    separator?: boolean;
};

export function Section({
    title = null,
    footer = null,
    children,
    className,
    sectionClassName,
    sectionContentClassName,
    testID,
    separator = true,
}: SectionProps) {
    const rows = Children.toArray(children).filter((child) => child != null);

    return (
        <View
            className={cn("gap-1.5", className)}
            testID={testID}
            collapsable={testID ? false : undefined}
        >
            {title &&
                (typeof title === "string" ? (
                    <Text
                        variant="caption"
                        className="px-1 uppercase tracking-wide"
                    >
                        {title}
                    </Text>
                ) : (
                    title
                ))}
            {rows.length > 0 ? (
                <Card className={cn("overflow-hidden", sectionClassName)}>
                    <CardContent className={sectionContentClassName}>
                        {rows.map((row, index) => (
                            <Fragment key={index}>
                                {row}
                                {index < rows.length - 1 && separator ? (
                                    <Separator className="mx-4 w-auto h-[0.5px]" />
                                ) : null}
                            </Fragment>
                        ))}
                    </CardContent>
                </Card>
            ) : null}
            {footer &&
                (typeof footer === "string" ? (
                    <Text variant="caption" className="px-1">
                        {footer}
                    </Text>
                ) : (
                    footer
                ))}
        </View>
    );
}
